import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  HStack,
  Text,
  Badge,
  useDisclosure,
  Container,
  Heading,
  Spinner,
  Textarea,
  Flex,
  Checkbox,
  Circle,
} from '@chakra-ui/react';
import { useAuth } from '../contexts/AuthContext';
import { updateUser, getOwnerUsers, getUnreadUserIds } from '../services/api';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const { currentUser, isFunctionEnabled } = useAuth();
  const navigate = useNavigate();

  const statusColors = {
    "初来店前": "gray",
    "初来店～契約(受注見込み薄)": "yellow",
    "初来店～契約(受注見込み高)": "green",
    "契約～設備確定": "blue",
    "設備確定～引き渡し": "purple",
    "引き渡し後": "teal",
    "失注": "red"
  };

  const [unreadUserIds, setUnreadUserIds] = useState([]);

  const fetchUsers = async () => {
    try {
      const data = await getOwnerUsers();
      setUsers(data);
      setError(null);
    } catch (err) {
      setError(err.message);
      toast({
        title: 'エラー',
        description: err.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchUnreadUserIds = async () => {
      // chat機能が無効な場合は未読ユーザー通知を取得しない
      if (!isFunctionEnabled('chat')) {
        setUnreadUserIds([]);
        return;
      }

      try {
        const data = await getUnreadUserIds();
        setUnreadUserIds(data.user_ids);
      } catch (error) {
        console.error('未読ユーザーIDの取得に失敗:', error);
        setUnreadUserIds([]);
      }
    };

    fetchUnreadUserIds();
    
    // chat機能が有効な場合のみ30秒ごとに更新
    if (isFunctionEnabled('chat')) {
      const interval = setInterval(fetchUnreadUserIds, 30000);
      return () => clearInterval(interval);
    }
  }, [isFunctionEnabled]);

  useEffect(() => {
    fetchUsers();
  }, [toast]);

  const handleUserClick = (userId) => {
    navigate(`/admin/user/${userId}`);
  };

  const handleEdit = (e, user) => {
    e.stopPropagation(); // クリックイベントの伝播を停止
    setSelectedUser(user);
    onOpen();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await updateUser(selectedUser.id, selectedUser);
      await fetchUsers();
      onClose();
      toast({
        title: '成功',
        description: '顧客情報を更新しました',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'エラー',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  if (loading) return (
    <Box display="flex" justifyContent="center" alignItems="center" minH="100vh">
      <Spinner size="xl" color="blue.500" />
    </Box>
  );

  if (error) return (
    <Box p={4}>
      <Text color="red.500">{error}</Text>
    </Box>
  );

  return (
    <Container maxW="container.xl" py={8}>
      <Heading mb={4}>お客様一覧</Heading>
      <HStack justify="flex-end" mb={4} p={4}>
        <Button bg="black" color="white" onClick={() => navigate('/admin/master-equipment')}>マスタ管理</Button>
        <Button bg="black" color="white" onClick={() => navigate('/admin/user-info')}>ユーザ管理</Button>
      </HStack>
      <Box overflowX="auto">
        <Table variant="simple">
          <Thead>
            <Tr>
              <Th>名前</Th>
              <Th>電話番号</Th>
              <Th>メールアドレス</Th>
              <Th>住所</Th>
              <Th>年齢</Th>
              <Th>家族構成</Th>
              <Th>土地所有</Th>
              <Th>建設予定地</Th>
              <Th>ステータス</Th>
              <Th>メモ</Th>
              <Th>操作</Th>
            </Tr>
          </Thead>
          <Tbody>
            {users.map((user) => (
              <Tr 
                key={user.id}
                onClick={() => handleUserClick(user.id)}
                cursor="pointer"
                _hover={{ bg: 'gray.50' }}
              >
                <Td>
                  {isFunctionEnabled('chat') && unreadUserIds.includes(user.id) && (
                    <Circle
                      size="8px"
                      bg="yellow.700"
                      position="absolute"
                      left="3px"
                    />
                  )}
                {user.name || '-'}
               </Td>
                <Td>{user.phone || '-'}</Td>
                <Td>{user.email || '-'}</Td>
                <Td>{user.address || '-'}</Td>
                <Td>{user.age || '-'}</Td>
                <Td>{user.family || '-'}</Td>
                <Td>{user.is_land_owner ? '有' : '無'}</Td>
                <Td>{user.construction_area || '-'}</Td>
                <Td>
                  <Badge colorScheme={statusColors[user.status] || 'gray'}>
                    {user.status || '未設定'}
                  </Badge>
                </Td>
                <Td>
                  <Text noOfLines={2}>{user.memo || '-'}</Text>
                </Td>
                <Td>
                  <Button 
                    size="sm" 
                    bg="black" 
                    color="white" 
                    onClick={(e) => handleEdit(e, user)}
                  >
                    編集
                  </Button>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>顧客情報の編集</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {selectedUser && (
              <form onSubmit={handleSubmit}>
                <VStack spacing={4}>
                  <FormControl>
                    <FormLabel>名前</FormLabel>
                    <Input
                      value={selectedUser.name || ''}
                      onChange={(e) => setSelectedUser({...selectedUser, name: e.target.value})}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>電話番号</FormLabel>
                    <Input
                      value={selectedUser.phone || ''}
                      onChange={(e) => setSelectedUser({...selectedUser, phone: e.target.value})}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>メールアドレス</FormLabel>
                    <Text>{selectedUser.email}</Text>
                  </FormControl>
                  <FormControl>
                    <FormLabel>住所</FormLabel>
                    <Input
                      value={selectedUser.address || ''}
                      onChange={(e) => setSelectedUser({...selectedUser, address: e.target.value})}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>年齢</FormLabel>
                    <Input
                      type="number"
                      value={selectedUser.age || ''}
                      onChange={(e) => setSelectedUser({...selectedUser, age: parseInt(e.target.value)})}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>家族構成</FormLabel>
                    <Input
                      value={selectedUser.family || ''}
                      onChange={(e) => setSelectedUser({...selectedUser, family: e.target.value})}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>土地所有</FormLabel>
                    <Checkbox
                      value={selectedUser.is_land_owner || ''}
                      isChecked={!!selectedUser.is_land_owner}
                      onChange={(e) => setSelectedUser({...selectedUser, is_land_owner: e.target.checked})}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>建設予定地</FormLabel>
                    <Input
                      value={selectedUser.construction_area || ''}
                      onChange={(e) => setSelectedUser({...selectedUser, construction_area: e.target.value})}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>ステータス</FormLabel>
                    <Select
                      value={selectedUser.status || ''}
                      onChange={(e) => setSelectedUser({...selectedUser, status: e.target.value})}
                    >
                      <option value="">未設定</option>
                      <option value="初来店前">初来店前</option>
                      <option value="初来店～契約(受注見込み薄)">初来店～契約(受注見込み薄)</option>
                      <option value="初来店～契約(受注見込み高)">初来店～契約(受注見込み高)</option>
                      <option value="契約～設備確定">契約～設備確定</option>
                      <option value="設備確定～引き渡し">設備確定～引き渡し</option>
                      <option value="引き渡し後">引き渡し後</option>
                      <option value="失注">失注</option>
                    </Select>
                  </FormControl>
                  <FormControl>
                    <FormLabel>メモ</FormLabel>
                    <Textarea
                      value={selectedUser.memo || ''}
                      onChange={(e) => setSelectedUser({...selectedUser, memo: e.target.value})}
                      placeholder="顧客に関するメモを入力してください"
                      rows={4}
                    />
                  </FormControl>
                  <Button type="submit" bg="black" color="white" width="full">
                    更新
                  </Button>
                </VStack>
              </form>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Container>
  );
};

export default UserManagement; 